import type { UserProfile } from './useApi';
import { getApiUrl } from './getApiUrl';

// Simple memoization for the session
let cachedProfile: UserProfile | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// CB:BEGIN graceful-profile-loading
export async function getMe(): Promise<UserProfile | null> {
  const now = Date.now();
  
  // Return cached profile if still valid
  if (cachedProfile && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedProfile;
  }
  
  try {
    // Try to get token from Clerk first
    const clerk = (window as any).Clerk;
    
    // CB:BEGIN improved-clerk-check
    // Better error handling and diagnostics
    if (!clerk) {
      if (import.meta.env.DEV) {
        console.warn('[getMe] Clerk not loaded yet');
      }
      return null;
    }
    
    // Check if user is signed in
    if (!clerk.user) {
      if (import.meta.env.DEV) {
        console.warn('[getMe] User not signed in');
      }
      return null;
    }
    
    // Check if session exists
    if (!clerk.session) {
      if (import.meta.env.DEV) {
        console.warn('[getMe] No active session');
      }
      return null;
    }
    // CB:END improved-clerk-check
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    // CB:BEGIN improved-token-retrieval
    // Get token with better error handling
    let token: string | null = null;
    try {
      token = await clerk.session.getToken();
      if (!token) {
        if (import.meta.env.DEV) {
          console.warn('[getMe] Failed to get token from Clerk session');
        }
        return null;
      }
      headers['Authorization'] = `Bearer ${token}`;
    } catch (tokenError: any) {
      if (import.meta.env.DEV) {
        console.error('[getMe] Token retrieval error:', tokenError?.message || tokenError);
      }
      return null;
    }
    // CB:END improved-token-retrieval

    const apiUrl = getApiUrl('/api/me');
    const response = await fetch(apiUrl, { 
      headers,
      credentials: 'include' // CB:BEGIN include-credentials
    });

    if (!response.ok) {
      // CB:BEGIN better-error-logging
      if (import.meta.env.DEV) {
        console.warn(`[getMe] API request failed: ${response.status} ${response.statusText}`, {
          url: apiUrl,
          hasToken: !!token,
          status: response.status
        });
      }
      // CB:END better-error-logging
      return null;
    }

    const raw = await response.json();
    // CB:BEGIN me-shape-compat
    // Normalize shape for both modern backend and server-simple test route
    const profile = {
      id: raw?.id || raw?.userId || raw?.clerkId || '',
      email: raw?.email || null,
      role: raw?.role || raw?.profile?.role || 'HOMEOWNER',
      userId: raw?.id || raw?.userId || raw?.clerkId || ''
    } as UserProfile;
    if (profile) {
      cachedProfile = profile;
      cacheTimestamp = now;
    }
    return profile; // can be null, caller must handle
    // CB:END me-shape-compat
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[getMe] Unexpected error:', error);
    }
    return null;
  }
}
// CB:END graceful-profile-loading

export function clearMeCache() {
  cachedProfile = null;
  cacheTimestamp = 0;
}

// CB:BEGIN clear-cache-for-demo
// Clear cache immediately for demo mode - moved to lazy initialization to avoid TDZ
// clearMeCache(); // Commented out to avoid TDZ - will be called when needed
// CB:END clear-cache-for-demo
